import React, { useState, useRef, useEffect } from 'react';
import './EmotionAIChat.css';

const EmotionAIChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: '👋 你好！我是你的情绪分析助手。你可以问我关于你的情绪记录的任何问题，比如：\n\n• "我最近的情绪状态怎么样？"\n• "分析一下我的情绪模式"\n• "我什么时候最开心？"\n• "帮我找到压力大的时候"',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // 调用AI助手API，AI会通过MCP查询用户的情绪数据
      const response = await fetch('/api/ai/emotion-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: inputText,
          conversationHistory: messages.slice(-5) // 发送最近5条消息作为上下文
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: data.response,
        timestamp: new Date(),
        mcpCalls: data.mcpCalls || [], // AI调用的MCP工具信息
        analysis: data.analysis || null // 情绪分析数据
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: '抱歉，我现在无法回应。请稍后再试。',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content) => {
    return content.split('\n').map((line, index) => (
      <div key={index}>
        {line}
        {index < content.split('\n').length - 1 && <br />}
      </div>
    ));
  };

  const renderMcpInfo = (mcpCalls) => {
    if (!mcpCalls || mcpCalls.length === 0) return null;
    
    return (
      <div className="mcp-info">
        <div className="mcp-header">🔍 查询了以下数据：</div>
        {mcpCalls.map((call, index) => (
          <div key={index} className="mcp-call">
            <span className="mcp-tool">{call.tool}</span>
            <span className="mcp-desc">{call.description}</span>
            {call.resultCount && (
              <span className="mcp-count">({call.resultCount} 条记录)</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const quickQuestions = [
    "我最近的情绪状态怎么样？",
    "分析一下我的情绪模式",
    "我什么时候最开心？",
    "帮我找到压力大的时候",
    "我的情绪变化趋势如何？"
  ];

  return (
    <div className="emotion-ai-chat">
      <div className="chat-header">
        <div className="header-info">
          <h2>🤖 情绪分析助手</h2>
          <p>基于你的情绪记录提供个性化分析和建议</p>
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-content">
              <div className="message-text">
                {formatMessage(message.content)}
              </div>
              {message.mcpCalls && renderMcpInfo(message.mcpCalls)}
              <div className="message-time">
                {message.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message ai">
            <div className="message-content">
              <div className="message-text">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                正在分析你的情绪数据...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {messages.length === 1 && (
        <div className="quick-questions">
          <div className="quick-questions-title">💡 试试这些问题：</div>
          <div className="quick-questions-list">
            {quickQuestions.map((question, index) => (
              <button
                key={index}
                className="quick-question"
                onClick={() => setInputText(question)}
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-input">
        <div className="input-container">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="问我关于你情绪的任何问题..."
            rows="1"
            disabled={isLoading}
          />
          <button 
            onClick={sendMessage}
            disabled={!inputText.trim() || isLoading}
            className="send-button"
          >
            {isLoading ? '⏳' : '发送'}
          </button>
        </div>
        <div className="input-hint">
          💡 我可以帮你分析情绪趋势、查找特定情绪记录、提供个性化建议等
        </div>
      </div>
    </div>
  );
};

export default EmotionAIChat;